import {
  HEARD_ABOUT_US_OPTIONS,
  type HeardAboutUs,
} from "@repo/vihangam-yog-shared";
import { useState } from "react";

import { track } from "../analytics";
import { Lotus } from "../components/Ornaments";
import { trpc } from "../trpc";

import { type EventPhase, type VyEvent } from "./eventsConfig";

/**
 * Ticket-request form for a community event. Renders on the
 * dedicated /events/<slug> registration page so a flyer or share
 * link can drop someone straight into the form with the event
 * details already on screen.
 *
 * adults + children is the source of truth; total people is computed
 * client-side and re-derived server-side (the backend's BAD_REQUEST
 * guard exercises on both sides). The backend reads the request
 * Origin to route NZ submissions through the NZ Postmark account, so
 * the ticket sends under the Vihangam Yog New Zealand brand.
 *
 * On success we replace the form with a ticket-style confirmation
 * card carrying the reference code so the visitor has something
 * concrete to remember even before the email lands.
 */
export function EventTicketForm({
  event,
  phase = "upcoming",
}: {
  event: VyEvent;
  /**
   * When "active", copy reframes "register" → "check in" so a walk-in
   * during the session understands they're confirming presence, not
   * promising future attendance. The backend mutation is the same.
   */
  phase?: EventPhase;
}) {
  const isCheckIn = phase === "active";
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [adults, setAdults] = useState(1);
  const [children, setChildren] = useState(0);
  const [heardAboutUs, setHeardAboutUs] = useState<HeardAboutUs | "">("");
  const [error, setError] = useState<string | null>(null);
  const totalPeople = adults + children;
  const register = trpc.registerForEvent.useMutation();

  function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    if (totalPeople < 1) {
      setError("Add at least one adult so we know who's coming.");
      return;
    }
    const trimmedName = name.trim();
    const trimmedEmail = email.trim();
    const trimmedPhone = phone.trim();
    if (!trimmedName) {
      setError("Your name helps us greet you at the door.");
      return;
    }
    if (!trimmedEmail) {
      setError("We send the ticket to this address.");
      return;
    }
    if (!heardAboutUs) {
      setError("Let us know how you heard about us.");
      return;
    }
    register.mutate(
      {
        eventSlug: event.slug,
        email: trimmedEmail,
        name: trimmedName,
        phone: trimmedPhone || undefined,
        totalPeople,
        adults,
        children,
        heardAboutUs,
      },
      {
        onSuccess: (data) => {
          track("event_ticket_requested", {
            slug: event.slug,
            already_registered: data.alreadyRegistered,
          });
        },
      },
    );
  }

  if (register.data) {
    return (
      <div className="border border-gold/50 bg-warm-white px-6 py-7 md:px-8 md:py-8">
        <Lotus size={32} className="mb-4 text-gold opacity-90" />
        <p className="text-[0.6875rem] font-semibold tracking-[0.28em] text-gold uppercase">
          {register.data.alreadyRegistered
            ? "You're already on the list"
            : isCheckIn
              ? "Checked in"
              : "Ticket sent"}
        </p>
        <p className="mt-3 text-[15px] leading-relaxed text-warm-brown md:text-base">
          {register.data.alreadyRegistered
            ? `We already have you down for this session, so we've re-sent your ticket to ${email.trim()}. Your reference is in that email.`
            : isCheckIn
              ? `You're checked in. We've also emailed a copy of your ticket to ${email.trim()} so you have it on file.`
              : `We've emailed your ticket to ${email.trim()}. Bring it to the door — or just give us your name; we'll find you.`}
        </p>
        {register.data.confirmationRef ? (
          <p className="mt-5 font-mono text-base tracking-wider text-warm-brown md:text-lg">
            {register.data.confirmationRef}
          </p>
        ) : null}
        <p className="mt-6 text-sm text-warm-brown-light">
          {isCheckIn
            ? "Anyone with you is on the list under your reference."
            : "Forward this email to anyone coming with you — they'll all be on the list under your reference."}
        </p>
      </div>
    );
  }

  const disabled = register.isPending;
  return (
    <form
      onSubmit={onSubmit}
      className="border border-(--color-hairline) bg-warm-white px-6 py-7 md:px-8 md:py-8"
    >
      <div className="grid gap-4 md:grid-cols-2">
        <label className="block">
          <span className="eyebrow">Your name</span>
          <input
            type="text"
            required
            autoComplete="name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            disabled={disabled}
            className="field-line mt-1 text-base"
            placeholder="First and last"
          />
        </label>
        <label className="block">
          <span className="eyebrow">Email</span>
          <input
            type="email"
            required
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={disabled}
            className="field-line mt-1 text-base"
            placeholder="you@example.com"
          />
        </label>
        <label className="block md:col-span-2">
          <span className="eyebrow">
            Phone <span className="text-warm-brown-light/60">(optional)</span>
          </span>
          <input
            type="tel"
            autoComplete="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            disabled={disabled}
            className="field-line mt-1 text-base"
            placeholder="So we can reach you about the day"
          />
        </label>
        <label className="block md:col-span-2">
          <span className="eyebrow">How did you hear about us?</span>
          <select
            required
            value={heardAboutUs}
            onChange={(e) =>
              // SAFETY: the select's options are exactly HEARD_ABOUT_US_OPTIONS plus the empty placeholder.
              setHeardAboutUs(e.target.value as HeardAboutUs | "")
            }
            disabled={disabled}
            className="field-line mt-1 text-base"
          >
            <option value="" disabled>
              Select one…
            </option>
            {HEARD_ABOUT_US_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </label>
        <label className="block">
          <span className="eyebrow">Adults (including you)</span>
          <input
            type="number"
            min={1}
            max={50}
            inputMode="numeric"
            value={adults}
            onChange={(e) =>
              setAdults(Math.max(0, Math.min(50, Number(e.target.value) || 0)))
            }
            disabled={disabled}
            className="field-line onum mt-1 text-base"
          />
        </label>
        <label className="block">
          <span className="eyebrow">Children</span>
          <input
            type="number"
            min={0}
            max={50}
            inputMode="numeric"
            value={children}
            onChange={(e) =>
              setChildren(
                Math.max(0, Math.min(50, Number(e.target.value) || 0)),
              )
            }
            disabled={disabled}
            className="field-line onum mt-1 text-base"
          />
        </label>
      </div>

      <p className="mt-4 text-sm text-warm-brown-light">
        Total people coming:{" "}
        <span className="font-medium text-warm-brown">{totalPeople}</span>
      </p>

      {error ? (
        <p role="alert" className="mt-3 text-sm text-gold md:text-base">
          {error}
        </p>
      ) : null}
      {register.error ? (
        <p role="alert" className="mt-3 text-sm text-gold md:text-base">
          {register.error.message}
        </p>
      ) : null}

      <button
        type="submit"
        disabled={disabled}
        className="mt-5 inline-flex items-center gap-2 rounded-full bg-warm-brown px-6 py-3 text-sm font-semibold tracking-wide text-cream transition-colors duration-300 hover:bg-gold disabled:opacity-60"
      >
        {disabled
          ? isCheckIn
            ? "Checking you in…"
            : "Sending…"
          : isCheckIn
            ? "Check me in"
            : "Send my ticket"}
        <span className="text-cream/70">→</span>
      </button>
      <p className="mt-3 text-xs text-warm-brown-light/80 md:text-sm">
        {isCheckIn
          ? "We'll log you in and email a copy of your ticket for the record. Free, no payment."
          : "We'll email a confirmation with your reference code. Free, no payment — we use this to know who to expect."}
      </p>
      <p className="mt-2 text-xs leading-relaxed text-warm-brown-light/70">
        Your details are only used to contact you about this and our other
        sessions. We never sell or share your information, and you can ask us to
        remove it at any time — handled in line with New Zealand's Privacy Act
        2020.
      </p>
    </form>
  );
}
